/**
 * DrawingConfirmModal.tsx - Confirmation Modal for Drawn Predictions
 * 
 * Shows Grok AI's interpretation of the user's drawing
 * Allows editing before submission
 */

import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// ============ Types ============

interface SequenceStep {
  step: number;
  direction: 'bullish' | 'bearish';
  target_price: number;
  target_date: string;
  description?: string;
}

interface GrokInterpretation {
  interpretation: string;
  prediction_type: string;
  direction: string;
  price_target: number;
  price_min: number;
  price_max: number;
  target_date: string;
  end_date: string;
  sequence_data: SequenceStep[] | null;
  confidence: number;
  userThesis?: string;
  drawing_points?: { price: number; date: string }[];
  // Vision API extras for freehand mode
  what_i_see?: string;
  emoji_summary?: string;
}

interface PredictionPoint {
  price: number;
  date: Date;
  x: number;
  y: number;
}

interface FreehandPath {
  points: { x: number; y: number }[];
  color: string;
}

interface DrawingConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (prediction: GrokInterpretation) => void;
  points: PredictionPoint[];
  drawingMode: 'points' | 'range' | 'pattern' | 'freehand';
  freehandPaths?: FreehandPath[];
  imageBase64?: string | null;
  currentPrice: number;
  theme: any;
}

// ============ Animations ============

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ============ Styled Components ============

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: ${fadeIn} 0.2s ease;
  padding: 20px;
`;

const Modal = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #fff;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
  
  .spinner {
    font-size: 32px;
    animation: ${pulse} 1s infinite;
    margin-bottom: 16px;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 20px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 8px;
  color: #f44336;
  margin-bottom: 16px;
`;

const InterpretationBox = styled.div`
  background: rgba(247, 147, 26, 0.1);
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const InterpretationLabel = styled.div`
  font-size: 12px;
  color: #f7931a;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InterpretationText = styled.p`
  color: #fff;
  font-size: 15px;
  line-height: 1.5;
  margin: 0;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const DetailCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
`;

const DetailLabel = styled.div`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const DetailValue = styled.div<{ $color?: string }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.$color || '#fff'};
`;

const SequenceList = styled.div`
  margin-bottom: 20px;
`;

const SequenceTitle = styled.div`
  font-size: 13px;
  color: #888;
  margin-bottom: 8px;
`;

const SequenceStep = styled.div<{ $direction: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  margin-bottom: 6px;
  border-left: 3px solid ${props => props.$direction === 'bullish' ? '#4caf50' : '#f44336'};
  
  .step-num {
    background: ${props => props.$direction === 'bullish' ? '#4caf50' : '#f44336'};
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
  }
  
  .step-details {
    flex: 1;
  }
  
  .step-price {
    font-weight: 600;
    color: ${props => props.$direction === 'bullish' ? '#4caf50' : '#f44336'};
  }
  
  .step-date {
    font-size: 12px;
    color: #888;
  }
`;

const ConfidenceMeter = styled.div<{ $confidence: number }>`
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  margin-bottom: 20px;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.$confidence * 100}%;
    background: ${props => 
      props.$confidence > 0.8 ? '#4caf50' : 
      props.$confidence > 0.5 ? '#f7931a' : '#f44336'};
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

const ConfidenceLabel = styled.div`
  font-size: 11px;
  color: #888;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'share' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  
  ${props => props.$variant === 'primary' ? `
    background: #f7931a;
    color: white;
    
    &:hover { background: #e8850f; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  ` : props.$variant === 'share' ? `
    background: rgba(29, 161, 242, 0.2);
    color: #1da1f2;
    border: 1px solid rgba(29, 161, 242, 0.3);
    
    &:hover { background: rgba(29, 161, 242, 0.3); }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #888;
    
    &:hover { background: rgba(255, 255, 255, 0.15); color: #fff; }
  `}
`;

const UserThesisSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ThesisLabel = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ThesisTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #f7931a;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const CharCount = styled.div<{ $over: boolean }>`
  font-size: 11px;
  color: ${props => props.$over ? '#f44336' : '#666'};
  text-align: right;
  margin-top: 4px;
`;

// ============ Helper Functions ============

const formatPrice = (price: number) => `$${price?.toLocaleString() || 'N/A'}`;
const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const getDirectionColor = (direction: string) => {
  switch (direction) {
    case 'bullish': return '#4caf50';
    case 'bearish': return '#f44336';
    case 'mixed': return '#f7931a';
    default: return '#888';
  }
};

const getTypeEmoji = (type: string) => {
  switch (type) {
    case 'target': return '🎯';
    case 'crash': return '📉';
    case 'sequence': return '📊';
    case 'top': return '🔝';
    case 'bottom': return '🔻';
    default: return '📈';
  }
};

// ============ Main Component ============

export const DrawingConfirmModal: React.FC<DrawingConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  points,
  drawingMode,
  freehandPaths,
  imageBase64,
  currentPrice,
  theme
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interpretation, setInterpretation] = useState<GrokInterpretation | null>(null);
  const [userThesis, setUserThesis] = useState('');
  const MAX_THESIS_LENGTH = 280; // Twitter-compatible
  
  // Track if we've already fetched for the current modal session
  const hasFetchedRef = useRef(false);
  const lastPointsHashRef = useRef<string>('');

  // Fetch Grok's interpretation when modal opens - ONLY ONCE per modal session
  useEffect(() => {
    // For freehand mode, check freehandPaths instead of points
    const hasContent = drawingMode === 'freehand' 
      ? (freehandPaths && freehandPaths.length > 0)
      : points.length > 0;
      
    if (!isOpen || !hasContent) {
      // Reset when modal closes
      if (!isOpen) {
        hasFetchedRef.current = false;
        lastPointsHashRef.current = '';
      }
      return;
    }

    // Create a hash to detect actual changes
    const pointsHash = drawingMode === 'freehand' 
      ? `freehand-${freehandPaths?.length || 0}-${imageBase64?.length || 0}`
      : JSON.stringify(points.map(p => ({ price: Math.round(p.price), date: p.date.toISOString().split('T')[0] })));
    
    // Skip if we've already fetched for these exact points
    if (hasFetchedRef.current && pointsHash === lastPointsHashRef.current) {
      console.log('🔄 Skipping duplicate Grok fetch - same data');
      return;
    }

    const fetchInterpretation = async () => {
      setLoading(true);
      setError(null);
      hasFetchedRef.current = true;
      lastPointsHashRef.current = pointsHash;

      try {
        // 🎨 FREEHAND MODE: Use Vision API
        if (drawingMode === 'freehand') {
          console.log('🎨 Freehand mode detected');
          console.log('   - Image base64:', imageBase64 ? `${imageBase64.length} chars` : 'NOT CAPTURED');
          console.log('   - Freehand paths:', freehandPaths?.length || 0);
          console.log('   - Points (should be 0):', points.length);
          
          if (imageBase64) {
            // Use Vision API with the captured image
            console.log('🎨 Using Vision API for freehand drawing');
            const response = await fetch('/api/grok/interpret-freehand-vision', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                imageBase64,
                currentPrice,
                freehandPaths
              })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
              throw new Error(data.error || 'Failed to interpret freehand drawing');
            }

            console.log('✅ Grok Vision interpretation received:', data.prediction_type);
          
            // Map vision response to standard interpretation format
            setInterpretation({
              interpretation: data.interpretation || data.what_i_see,
              prediction_type: data.prediction_type || 'sentiment',
              direction: data.direction || 'bullish',
              price_target: data.price_target || currentPrice,
              price_min: data.price_min || data.price_target || currentPrice,
              price_max: data.price_max || data.price_target || currentPrice,
              target_date: data.target_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              end_date: data.end_date || '',
              sequence_data: null,
              confidence: data.confidence || 0.5,
              what_i_see: data.what_i_see,
              emoji_summary: data.emoji_summary
            });
          } else {
            // Freehand mode but no image captured - use sentiment fallback
            console.log('⚠️ Freehand mode but no image captured, using fallback');
            setInterpretation({
              interpretation: "I can see you've drawn something creative! 🎨 Without the image capture working, I'll interpret this as bullish vibes for Bitcoin!",
              prediction_type: 'sentiment',
              direction: 'bullish',
              price_target: currentPrice,
              price_min: currentPrice,
              price_max: currentPrice,
              target_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              end_date: '',
              sequence_data: null,
              confidence: 0.3,
              what_i_see: 'Freehand drawing (image capture failed)',
              emoji_summary: '🎨'
            });
          }
        } else {
          // 📍 STANDARD MODE (points, range, pattern): Use text-based interpretation
          console.log('🤖 Fetching Grok interpretation for', points.length, 'points, mode:', drawingMode);
          const response = await fetch('/api/grok/interpret-drawing', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              points: points.map(p => ({
                price: p.price,
                date: p.date.toISOString()
              })),
              currentPrice,
              drawingMode
            })
          });

          const data = await response.json();

          if (!response.ok || !data.success) {
            throw new Error(data.error || 'Failed to interpret drawing');
          }

          console.log('✅ Grok interpretation received:', data.prediction_type);
          setInterpretation(data);
        }
      } catch (err: any) {
        console.error('❌ Error fetching interpretation:', err);
        setError(err.message || 'Failed to get AI interpretation');
      } finally {
        setLoading(false);
      }
    };

    fetchInterpretation();
  }, [isOpen, points, currentPrice, drawingMode, freehandPaths, imageBase64]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (interpretation) {
      // Merge user thesis with interpretation
      onConfirm({
        ...interpretation,
        userThesis: userThesis.trim(),
        drawing_points: points.map(p => ({
          price: p.price,
          date: p.date.toISOString()
        }))
      });
    }
  };

  return (
    <Overlay onClick={(e) => {
      // Don't close on outside click while loading - user might accidentally dismiss
      if (loading) return;
      if (e.target === e.currentTarget) onClose();
    }}>
      <Modal>
        <ModalHeader>
          <ModalTitle>
            {loading ? '⏳ Analyzing...' : error ? '❌ Error' : '🤖 Grok\'s Interpretation'}
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading && (
            <LoadingState>
              <div className="spinner">🤖</div>
              <div>
                {drawingMode === 'freehand' 
                  ? 'Grok is looking at your artwork with Vision AI...'
                  : 'Analyzing your prediction with Grok AI...'}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                {drawingMode === 'freehand'
                  ? 'Vision API takes 10-20 seconds. Don\'t close this window!'
                  : 'This may take 5-15 seconds. Don\'t close this window.'}
              </div>
            </LoadingState>
          )}

          {error && (
            <ErrorState>
              ⚠️ {error}
              <br />
              <Button style={{ marginTop: '12px' }} onClick={onClose}>
                Try Again
              </Button>
            </ErrorState>
          )}

          {!loading && !error && interpretation && (
            <>
              {/* Confidence meter */}
              <ConfidenceLabel>
                <span>Confidence</span>
                <span>{Math.round((interpretation.confidence || 0.5) * 100)}%</span>
              </ConfidenceLabel>
              <ConfidenceMeter $confidence={interpretation.confidence || 0.5} />

              {/* Main interpretation */}
              <InterpretationBox>
                <InterpretationLabel>
                  {getTypeEmoji(interpretation.prediction_type)} AI Interpretation
                </InterpretationLabel>
                {/* For freehand mode: Show what Grok sees first */}
                {interpretation.what_i_see && (
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#888', 
                    marginBottom: '8px',
                    fontStyle: 'italic'
                  }}>
                    👁️ I see: {interpretation.what_i_see} {interpretation.emoji_summary || ''}
                  </div>
                )}
                <InterpretationText>
                  "{interpretation.interpretation}"
                </InterpretationText>
              </InterpretationBox>

              {/* Details grid */}
              <DetailsGrid>
                <DetailCard>
                  <DetailLabel>Type</DetailLabel>
                  <DetailValue>
                    {interpretation.prediction_type?.toUpperCase()}
                  </DetailValue>
                </DetailCard>
                <DetailCard>
                  <DetailLabel>Direction</DetailLabel>
                  <DetailValue $color={getDirectionColor(interpretation.direction)}>
                    {interpretation.direction === 'bullish' ? '📈 Bullish' :
                     interpretation.direction === 'bearish' ? '📉 Bearish' :
                     interpretation.direction === 'mixed' ? '📊 Mixed' : '➡️ Neutral'}
                  </DetailValue>
                </DetailCard>
                <DetailCard>
                  <DetailLabel>Target Price</DetailLabel>
                  <DetailValue $color={getDirectionColor(interpretation.direction)}>
                    {formatPrice(interpretation.price_target)}
                  </DetailValue>
                </DetailCard>
                <DetailCard>
                  <DetailLabel>Target Date</DetailLabel>
                  <DetailValue>
                    {formatDate(interpretation.end_date || interpretation.target_date)}
                  </DetailValue>
                </DetailCard>
              </DetailsGrid>

              {/* Sequence steps if applicable */}
              {interpretation.sequence_data && interpretation.sequence_data.length > 0 && (
                <SequenceList>
                  <SequenceTitle>Prediction Steps:</SequenceTitle>
                  {interpretation.sequence_data.map((step, i) => (
                    <SequenceStep key={i} $direction={step.direction}>
                      <div className="step-num">{step.step}</div>
                      <div className="step-details">
                        <div className="step-price">
                          {step.direction === 'bullish' ? '↗' : '↘'} {formatPrice(step.target_price)}
                        </div>
                        <div className="step-date">by {formatDate(step.target_date)}</div>
                      </div>
                    </SequenceStep>
                  ))}
                </SequenceList>
              )}

              {/* Points summary */}
              <DetailCard style={{ marginBottom: 0 }}>
                <DetailLabel>Your Points ({points.length})</DetailLabel>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {points.map((p, i) => (
                    <span key={i}>
                      {formatPrice(p.price)} ({formatDate(p.date.toISOString())})
                      {i < points.length - 1 ? ' → ' : ''}
                    </span>
                  ))}
                </div>
              </DetailCard>

              {/* User thesis text box */}
              <UserThesisSection>
                <ThesisLabel>
                  ✍️ Add Your Thesis (optional)
                </ThesisLabel>
                <ThesisTextarea
                  value={userThesis}
                  onChange={(e) => setUserThesis(e.target.value.slice(0, MAX_THESIS_LENGTH + 50))}
                  placeholder="Why do you think Bitcoin will do this? Share your reasoning..."
                  maxLength={MAX_THESIS_LENGTH + 50}
                />
                <CharCount $over={userThesis.length > MAX_THESIS_LENGTH}>
                  {userThesis.length}/{MAX_THESIS_LENGTH} {userThesis.length > MAX_THESIS_LENGTH && '(will be truncated)'}
                </CharCount>
              </UserThesisSection>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            $variant="primary" 
            onClick={handleConfirm}
            disabled={loading || !!error || !interpretation}
          >
            ✓ Submit Prediction
          </Button>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default DrawingConfirmModal;
